/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { capitalize, isNil, last } from 'lodash';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectAllProtocolsModalRateType,
  selectAllProtocolsModalTimestamp,
  selectAllProtocolsModalUseOtherTerminalHistory,
  selectAllProtocolsModalVisible,
  selectOtherTerminalHistory,
  selectTerminalHistory,
} from '../../../../store';
import { setTheAllProtocolModalData } from '../../../../store/terminalHistory/actions';
import { bpsToUnitString } from '../../../../utils/DataRateUtils';
import { dateToUTCDate, dateToUTCTime } from '../../../../utils/DateTimeUtils';
import Scrollbars from '../../../common/layout/Scrollbars';
import appTheme from '../../../common/theme/appTheme.json';
import ChartLegendTooltipContent, {
  ChartLegendTooltipEntry,
} from '../charts/ChartCustomComponents/ChartLegendTooltipContent';

function AllProtocolsModal() {
  const isVisible = useSelector(selectAllProtocolsModalVisible);
  const timestamp = useSelector(selectAllProtocolsModalTimestamp, shallowEqual);
  const rateKey = useSelector(selectAllProtocolsModalRateType, shallowEqual);
  const isOtherTermHistoryUsed = useSelector(selectAllProtocolsModalUseOtherTerminalHistory, shallowEqual);
  const rawTerminalHistory = useSelector(selectTerminalHistory, shallowEqual);
  const otherRawTerminalHistory = useSelector(selectOtherTerminalHistory, shallowEqual);

  // const dataRates = useSelector(selectAllProtocolsModalDataRates, shallowEqual);

  const dispatch = useDispatch();

  let dataRates: { [key: string]: number } = {};

  if (timestamp && rateKey) {
    if (!isOtherTermHistoryUsed) {
      dataRates =
        rawTerminalHistory[timestamp] && rawTerminalHistory[timestamp][rateKey]
          ? (rawTerminalHistory[timestamp][rateKey] as { [key: string]: number })
          : {};
    } else {
      dataRates =
        otherRawTerminalHistory[timestamp] && otherRawTerminalHistory[timestamp][rateKey]
          ? (otherRawTerminalHistory[timestamp][rateKey] as { [key: string]: number })
          : {};
    }
  }

  const hasData = !isNil(timestamp) && !isNil(dataRates) && Object.keys(dataRates).length > 0;
  const totalUsage = hasData
    ? Object.values(dataRates || {}).reduce((prev: number, dataRate: number) => prev + dataRate, 0)
    : 0;

  const onClose = () => {
    dispatch(setTheAllProtocolModalData(undefined, undefined, undefined));
  };

  return (
    <Modal open={isVisible && hasData} onClose={onClose} aria-labelledby={`modal-title`}>
      <Box
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: '316px',
          maxHeight: '472px',
          backgroundColor: '#000000',
          borderRadius: '8px',
          padding: '16px 2px 22px 16px',
          boxSizing: 'border-box',
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            id="modal-title"
            variant="h4"
            component="h4"
            sx={{
              fontFamily: appTheme.viasatFontFamilyRegular,
              fontStyle: 'normal',
              fontWeight: 800,
              fontSize: '24px',
              lineHeight: '32px',
              color: appTheme.primaryTextColor,
            }}
          >
            All Protocols
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ marginRight: '14px', padding: 0, color: appTheme.secondaryHeaderTextColor, width: 22, height: 22 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Typography
          id="modal-timestamp"
          variant="h6"
          component="h6"
          textTransform={'uppercase'}
          sx={{
            fontFamily: appTheme.viasatFontFamilyRegular,
            fontStyle: 'normal',
            fontWeight: 800,
            fontSize: '12px',
            lineHeight: '20px',
            color: appTheme.secondaryHeaderTextColor,
            marginBottom: '16px',
          }}
        >
          {`${dateToUTCDate(new Date((timestamp as number) * 1000))} ${dateToUTCTime(
            new Date((timestamp as number) * 1000),
          )}`}
        </Typography>

        {/* Modal Body */}
        <Scrollbars autoHeight autoHeightMax="370px">
          <Box
            sx={{
              display: 'flex',
              paddingRight: '18px',
              paddingLeft: '0px',
            }}
          >
            <ChartLegendTooltipContent
              entries={Object.keys(dataRates)
                .sort((a: string, b: string) => (dataRates[a] < dataRates[b] ? 1 : -1))
                .map(
                  (key: string) =>
                    ({
                      delimitedLastIndexDisplayName: capitalize(last(key.split('/'))),
                      nonDelimintedName: key,
                      value: dataRates[key],
                      dislayValue: bpsToUnitString(dataRates[key]),
                    } as ChartLegendTooltipEntry),
                )}
              totalUsage={totalUsage}
              showGraphs
            />
          </Box>
        </Scrollbars>
      </Box>
    </Modal>
  );
}

export default AllProtocolsModal;
